import React, { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { supabase } from "../lib/supabase";

const ContactForm = () => {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", company: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("contacts").insert({
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone || null,
      company: form.company || null,
      message: form.message,
    });
    setLoading(false);
    if (error) {
      console.error("[contact] supabase insert failed:", error);
      toast.error(`Submit failed: ${error.message || error.code || "unknown"}`);
      return;
    }
    toast.success("Message received. We'll be in touch shortly.");
    setSubmitted(true);
    setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", message: "" });
  };

  if (submitted) {
    return (
      <div
        data-testid="contact-success"
        className="bg-white border-2 border-[#4ADE80] p-10 text-center shadow-pixel"
      >
        <p className="font-pixel text-[#15803D] text-sm mb-3">MESSAGE_RECEIVED</p>
        <p className="font-arcade text-[#0A0A0A] text-2xl mb-5">We'll be in touch shortly.</p>
        <button
          onClick={() => setSubmitted(false)}
          className="font-arcade text-xl text-[#15803D] border-2 border-[#4ADE80] px-6 py-3 hover:bg-[#4ADE80] hover:text-black transition-colors duration-150"
        >
          SEND ANOTHER
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit}
      className="bg-white border-2 border-[#D4D4D4] p-8 space-y-5"
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-arcade text-base text-[#0A0A0A]/70 uppercase block mb-2">
            First Name <span className="text-[#15803D]">*</span>
          </label>
          <input
            type="text"
            name="first_name"
            value={form.first_name}
            onChange={handleChange}
            placeholder="Aarav"
            data-testid="contact-first-name"
            className="w-full bg-white border-2 border-[#D4D4D4] text-[#15803D] font-terminal text-sm p-3 focus:border-[#4ADE80] focus:outline-none placeholder:text-[#0A0A0A]/20 rounded-none"
          />
        </div>
        <div>
          <label className="font-arcade text-base text-[#0A0A0A]/70 uppercase block mb-2">
            Last Name <span className="text-[#15803D]">*</span>
          </label>
          <input
            type="text"
            name="last_name"
            value={form.last_name}
            onChange={handleChange}
            placeholder="Sharma"
            data-testid="contact-last-name"
            className="w-full bg-white border-2 border-[#D4D4D4] text-[#15803D] font-terminal text-sm p-3 focus:border-[#4ADE80] focus:outline-none placeholder:text-[#0A0A0A]/20 rounded-none"
          />
        </div>
      </div>

      <div>
        <label className="font-arcade text-base text-[#0A0A0A]/70 uppercase block mb-2">
          Email <span className="text-[#15803D]">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="aarav@company.com"
          data-testid="contact-email"
          className="w-full bg-white border-2 border-[#D4D4D4] text-[#15803D] font-terminal text-sm p-3 focus:border-[#4ADE80] focus:outline-none placeholder:text-[#0A0A0A]/20 rounded-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-arcade text-base text-[#0A0A0A]/70 uppercase block mb-2">Phone</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            data-testid="contact-phone"
            className="w-full bg-white border-2 border-[#D4D4D4] text-[#15803D] font-terminal text-sm p-3 focus:border-[#4ADE80] focus:outline-none placeholder:text-[#0A0A0A]/20 rounded-none"
          />
        </div>
        <div>
          <label className="font-arcade text-base text-[#0A0A0A]/70 uppercase block mb-2">Company</label>
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={handleChange}
            placeholder="Acme Inc."
            data-testid="contact-company"
            className="w-full bg-white border-2 border-[#D4D4D4] text-[#15803D] font-terminal text-sm p-3 focus:border-[#4ADE80] focus:outline-none placeholder:text-[#0A0A0A]/20 rounded-none"
          />
        </div>
      </div>

      <div>
        <label className="font-arcade text-base text-[#0A0A0A]/70 uppercase block mb-2">
          Message <span className="text-[#15803D]">*</span>
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          rows={5}
          placeholder="Tell us about your project..."
          data-testid="contact-message"
          className="w-full bg-white border-2 border-[#D4D4D4] text-[#15803D] font-terminal text-sm p-3 focus:border-[#4ADE80] focus:outline-none placeholder:text-[#0A0A0A]/20 rounded-none resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit"
        className="w-full flex items-center justify-center gap-3 bg-[#4ADE80] text-black font-arcade text-xl uppercase tracking-widest py-4 border-2 border-[#4ADE80] hover:bg-transparent hover:text-[#15803D] hover:shadow-[4px_4px_0px_0px_rgba(74,222,128,0.5)] transition-colors duration-150 btn-press disabled:opacity-50"
      >
        {loading ? "TRANSMITTING..." : (
          <>
            SEND MESSAGE <Send size={16} />
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
